import { useMutation, useQuery } from "@tanstack/react-query";
import useAuthHttp from "../hooks/useAuthHttp";

export interface IIInvestorProfileForm {
    age_range: { id: string; name: string };
    global_investment_horizon: { id: string; name: string };
    monthly_net_income: { id: string; name: string };
    new_investment_allocation_pct: number;
    market_experience: { id: string; name: string };
    reaction_to_loss: { id: string; name: string };
    main_goal: { id: string; name: string };
    liquidity_need: { id: string; name: string };
    financial_knowledge: { id: string; name: string };
    max_annual_loss: { id: string; name: string };
    illiquidity_tolerance: { id: string; name: string };
    impact_of_real_estate_loss: { id: string; name: string };
}

export interface IIInvestorProfile {
    age_range: string;
    global_investment_horizon: string;
    monthly_net_income: string;
    new_investment_allocation_pct: number;
    market_experience: string;
    reaction_to_loss: string;
    main_goal: string;
    liquidity_need: string;
    financial_knowledge: string;
    max_annual_loss: string;
    illiquidity_tolerance: string;
    impact_of_real_estate_loss: string;
}

export interface IIInvestorProfileResponse {
    age_range: string;
    global_investment_horizon: string;
    monthly_net_income: string;
    new_investment_allocation_pct: number;
    market_experience: string;
    reaction_to_loss: string;
    main_goal: string;
    liquidity_need: string;
    financial_knowledge: string;
    max_annual_loss: string;
    illiquidity_tolerance: string;
    impact_of_real_estate_loss: string;
    profile: string;
    score_calculated: number;
    score_percentage: number;
}

export interface IInvestorProfileInsightsResult {
    portfolio_insight: {
        id: string;
        user_id: string;
        insight_date: string;
        last_update: string;
        status: string;
        portfolio_data: {
            profile: {
                risk_level: string;
                goal: string;
                horizon_years: number;
            };
            current_alloc: {
                renda_fixa: number;
                renda_variavel: number;
                fii: number;
                cripto: number;
                veiculos: number;
                imoveis: number;
            };
            metrics: {
                sharpe: number;
                var_95: number;
                drawdown: number;
                liquidez_lenta: number;
                taxa_media: number;
            };
            market_context: {
                selic: number;
                ipca: number;
                volatilidade_mensal: number;
            };
            portfolio_summary: {
                total_value: number;
                total_assets: number;
                total_vehicles: number;
                total_properties: number;
            };
            portfolio_events: {
                total_aportes: number;
                total_saques: number;
                total_rendimentos: number;
                liquido_investido: number;
            };
        };
        openai_insight: {
            status: string;
            risco: {
                nivel: string;
                explicacao: string;
            };
            liquidez: {
                baixa: number;
                comentario: string;
            };
            diversificacao: {
                comentario: string;
            };
            insight_educativo: string;
            aviso: string;
        };
        metadata: {
            created_at: string;
            updated_at: string;
            insight_version: string;
        };
    };
}

export const useGetInvestorProfile = () => {
    const authHttp = useAuthHttp();

    return useQuery({
        queryKey: ["investor-profile"],
        retry: false,
        queryFn: async () => {
            return await authHttp.doGet<IIInvestorProfileResponse>("/user/suitability");
        }
    });
}

export const useGetInvestorProfileInsights = () => {
    const http = useAuthHttp();

    return useQuery({
        queryKey: ["investorProfileInsights"],
        retry: false,
        queryFn: async () => {
            return await http.doGet<IInvestorProfileInsightsResult>(`/user/portfolio_insight`);
        }
    });
}

export const useUpsertInvestorProfileMutation = (onSuccess: () => void, onError: (error: Error) => void) => {
    const authHttp = useAuthHttp();

    return useMutation({
        mutationFn: async (values: IIInvestorProfile) => {
            return await authHttp.doPost<IIInvestorProfile, any>("/user/suitability", values);
        },
        onSuccess: () => {
            onSuccess();
        },
        onError: (error) => {
            onError(error as Error);
        }
    });
}